import { useMemo, useState } from "react";
import { useData } from "@/context/DataContext";
import Timeline from "@/components/Timeline";
import TaskList from "@/components/TaskList";
import { todayISO, humanDate, addDays, isoDate, parseISO, longDate } from "@/lib/date";
import { CaretLeft, CaretRight, Lightning, MagicWand } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function TodayPage() {
  const { events, tasks, routines, applyRoutine, refresh } = useData();
  const [date, setDate] = useState(todayISO());
  const [arranging, setArranging] = useState(false);

  const dayEvents = useMemo(() => events.filter((e) => e.date === date), [events, date]);
  const dayTasks = useMemo(
    () => tasks.filter((t) => t.date === date && (t.scope || "day") === "day"),
    [tasks, date]
  );

  const shiftDay = (delta) => setDate(isoDate(addDays(parseISO(date), delta)));

  const applyR = async (r) => {
    try {
      await applyRoutine(r.id, date);
      toast.success(`${r.name} toegevoegd aan ${humanDate(date).toLowerCase()}`);
    } catch { toast.error("Toevoegen mislukt"); }
  };

  const autoArrange = async () => {
    setArranging(true);
    try {
      const { data } = await api.post("/schedule/auto-arrange", { date });
      const count = data.placed?.length || 0;
      if (count === 0) toast.info("Geen flexibele taken om in te plannen voor deze dag");
      else toast.success(`${count} ta${count === 1 ? "ak" : "ken"} ingepland`);
      await refresh();
    } catch (e) {
      toast.error("Automatisch plannen mislukt");
    } finally { setArranging(false); }
  };

  return (
    <div className="p-5 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Commandocentrum</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-50 mt-1">
            {humanDate(date)}
          </h1>
          <div className="text-sm text-slate-500 mt-1">{longDate(date)}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="today-auto-arrange" variant="outline" onClick={autoArrange} disabled={arranging}
            className="border-slate-800 bg-[#121C2F] hover:bg-slate-800 text-slate-200 text-xs gap-2">
            <MagicWand size={14} weight="duotone" /> {arranging ? "Bezig..." : "Automatisch plannen"}
          </Button>
          <Button data-testid="today-prev-day" variant="outline" size="icon"
            onClick={() => shiftDay(-1)} className="border-slate-800 bg-[#121C2F] hover:bg-slate-800 text-slate-300">
            <CaretLeft size={16} />
          </Button>
          <Button data-testid="today-today-btn" variant="outline"
            onClick={() => setDate(todayISO())} className="border-slate-800 bg-[#121C2F] hover:bg-slate-800 text-slate-200 text-xs">
            Vandaag
          </Button>
          <Button data-testid="today-next-day" variant="outline" size="icon"
            onClick={() => shiftDay(1)} className="border-slate-800 bg-[#121C2F] hover:bg-slate-800 text-slate-300">
            <CaretRight size={16} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <Timeline date={date} events={dayEvents} />
        </div>

        <div className="lg:col-span-4 space-y-6">
          <TaskList tasks={dayTasks} title="Takenlijst" />

          {routines.length > 0 && (
            <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Routines</div>
                  <div className="text-slate-100 font-medium">Met één tik toevoegen</div>
                </div>
                <Lightning size={16} className="text-blue-400" />
              </div>
              <div className="space-y-2">
                {routines.map((r) => (
                  <button
                    key={r.id}
                    data-testid={`routine-apply-${r.id}`}
                    onClick={() => applyR(r)}
                    className="w-full text-left border border-slate-800 rounded-lg px-3 py-2.5 bg-slate-900/40 hover:bg-slate-800/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-100 font-medium">{r.name}</span>
                      <span className="text-[10px] uppercase tracking-widest text-blue-400">Toevoegen</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {r.items?.length || 0} stappen · start {r.default_start || "07:00"}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
